import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import FormatAlignCenter from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeft from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRight from "@mui/icons-material/FormatAlignRight";
import { $getSelection, FORMAT_ELEMENT_COMMAND } from "lexical";
import { useCallback, useMemo } from "react";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import { ToolbarDropDown } from "./toolbar-drop-down";
import { ToolbarIconButton } from "./toolbar-icon-button";

import type { ToolbarProps } from "./props";
import type { FC } from "react";

export const AlignmentButtons: FC<
  ToolbarProps & {
    active?: "left" | "center" | "right" | null;
  }
> = ({ disabled, mode, active }) => {
  const dialog = useDialog();
  const [editor] = useLexicalComposerContext();
  const dispatch = useCallback(
    (align: "left" | "center" | "right") => {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
      dialog.handleClose();
    },
    [editor, dialog],
  );

  const { icon, isCenter, isLeft, isRight } = useMemo(() => {
    switch (active) {
      case "left":
        return {
          icon: <FormatAlignLeft fontSize="medium" />,
          isLeft: true,
          isCenter: false,
          isRight: false,
        };
      case "center":
        return {
          icon: <FormatAlignCenter fontSize="medium" />,
          isCenter: true,
          isLeft: false,
          isRight: false,
        };
      case "right":
        return {
          icon: <FormatAlignRight fontSize="medium" />,
          isRight: true,
          isLeft: false,
          isCenter: false,
        };
      default:
        return { icon: <FormatAlignLeft fontSize="medium" />, value: null };
    }
  }, [active]);

  return (
    <>
      <ToolbarDropDown
        data-analytics-id={`lexical-${mode}-dropdown-alignment`}
        data-testid={`${mode}-dropdown-alignment`}
        icon={icon}
        data-test-value={active}
        heading=""
        disabled={disabled}
        open={dialog.open}
        setOpen={dialog.setOpen}
        mode={mode}
      >
        <ToolbarIconButton
          data-analytics-id={`lexical-${mode}-align-left`}
          disabled={disabled}
          label="Left align"
          data-testid={`${mode}-button-left-align`}
          aria-label="left align"
          data-active={isLeft}
          aria-pressed={isLeft}
          tabIndex={-1}
          sx={{
            backgroundColor: isLeft ? "background.active" : "background.neutral",
            justifyContent: "start",
          }}
          icon={<FormatAlignLeft fontSize="medium" />}
          onClick={() => dispatch("left")}
        />
        <ToolbarIconButton
          data-analytics-id={`lexical-${mode}-align-center`}
          disabled={disabled}
          label="Center align"
          data-testid={`${mode}-button-center-align`}
          aria-label="center align"
          data-active={isCenter}
          aria-pressed={isCenter}
          tabIndex={-1}
          sx={{
            backgroundColor: isCenter ? "background.active" : "background.neutral",
            justifyContent: "start",
          }}
          icon={<FormatAlignCenter fontSize="medium" />}
          onClick={() => dispatch("center")}
        />
        <ToolbarIconButton
          data-analytics-id={`lexical-${mode}-align-right`}
          disabled={disabled}
          label="Right align"
          data-testid={`${mode}-button-right-align`}
          aria-label="right align"
          data-active={isRight}
          aria-pressed={isRight}
          tabIndex={-1}
          sx={{
            backgroundColor: isRight ? "background.active" : "background.neutral",
            justifyContent: "start",
          }}
          icon={<FormatAlignRight fontSize="medium" />}
          onClick={() => dispatch("right")}
        />
      </ToolbarDropDown>
    </>
  );
};

export const $currentAlignment = (): "left" | "center" | "right" | null => {
  const selection = $getSelection();
  if (selection) {
    const node = selection.getNodes()?.[0];
    // Currently I can't find a way to get the alignment of a text node.
    if ($isDecoratorBlockNode(node)) {
      if (node.__format === "left") {
        return "left";
      }
      if (node.__format === "center") {
        return "center";
      }
      if (node.__format === "right") {
        return "right";
      }
    }
  }
  return null;
};
